import { IsoCountryCode } from 'generated/graphql-gateway';
import { getDobErrors, getLegacyDobErrors } from 'pages/authentication/sign-up/dobValidation';
import { t } from 'state/intl/translations';
import { Region } from 'utils/environment/types';
import {
  COUNTRY_BASED_ZIP_CODE_REGEX,
  getPhoneNumberErrorMessage,
  isEmailValid,
  isNameInputValid,
  isPhoneNumberValid,
} from 'utils/form';
import { validationConfig } from 'utils/form/user-profile-validation-config/validation';

import { IUserFormData } from './types';

/* ======== Helpers ======== */
export const isRequired = (
  required: boolean | { readonly locales: readonly string[] },
  region: Region
) => required === true || (required !== false && required.locales.includes(region));

/*  ======== Form Validation ======== */
export interface INameAndEmailParams {
  formData: IUserFormData;
}

export function validateEmail({ formData }: INameAndEmailParams) {
  if (!formData.email.trim()) {
    return { email: t('emailRequiredError') };
  }

  if (!isEmailValid(formData.email)) {
    return { email: t('notValidEmailError') };
  }

  return {};
}

export function validateName(nameParams: INameAndEmailParams) {
  if (!nameParams.formData.name.trim()) {
    return {
      name: t('nameRequiredError'),
    };
  } else if (!isNameInputValid(nameParams.formData.name)) {
    return {
      name: t('notValidNameError'),
    };
  }

  return {};
}

export interface IPhoneDobZipParams extends INameAndEmailParams {
  region: Region;
  simpleValidation?: boolean;
}
export async function validatePhoneNumber({
  formData,
  region,
  simpleValidation,
}: IPhoneDobZipParams) {
  const phoneNumberValidation =
    isRequired(validationConfig.phoneNumber.required, region) || formData.phoneNumber
      ? await isPhoneNumberValid({
          phoneNumber: formData.phoneNumber,
          country: region,
          simpleValidation,
        })
      : { valid: true };

  if (!phoneNumberValidation.valid) {
    return {
      phoneNumber: getPhoneNumberErrorMessage(phoneNumberValidation.error),
    };
  }

  return {};
}

export function validateDateOfBirth(dobParams: IPhoneDobZipParams) {
  let dobError;
  // ! The typeof === object conditional may be able to be removed at this point
  if (typeof dobParams.formData.dob === 'object') {
    dobError = getDobErrors(dobParams.formData.dob, dobParams.region) ?? undefined;
  } else {
    dobError = getLegacyDobErrors(dobParams.formData.dob) ?? undefined;
  }

  return dobError ? { dob: dobError } : {};
}

export function validateZipCode({ formData, region }: IPhoneDobZipParams) {
  let zipCodeError;

  if (isRequired(validationConfig.zipcode.required, region) && !formData.zipcode) {
    zipCodeError =
      formData.isoCountryCode === IsoCountryCode.USA
        ? t('zipCodeRequiredError')
        : t('postalCodeRequiredError');
  }

  const regex = COUNTRY_BASED_ZIP_CODE_REGEX[formData.isoCountryCode];

  if (formData.zipcode && formData.isoCountryCode && !regex.test(formData.zipcode.toUpperCase())) {
    zipCodeError =
      formData.isoCountryCode === IsoCountryCode.USA ? t('zipCodeInvalid') : t('postalCodeInvalid');
  }

  return zipCodeError ? { zipcode: zipCodeError } : {};
}

export const allValidationMethods = {
  validateEmail,
  validateName,
  validatePhoneNumber,
  validateDateOfBirth,
  validateZipCode,
};
